import { defineStore } from "pinia";
import { reactive, ref } from "vue";

import { documentAgentSessionRepository } from "@/cache/documentAgentSessionRepository";
import type { ApiError, AsyncStatus } from "@/types/api";
import type {
  DocumentAgentMessage,
  DocumentAgentSessionRecord,
  DocumentBlock,
  DocumentFormatAction,
  DocumentMutationState,
  DocumentPack,
  DocumentSaveState,
  DocumentType,
  EditorTab,
  SelectionModifiers,
} from "@/types/documents";

export interface DocumentRequestSnapshot {
  packId: string;
  documentType: DocumentType;
  blocks: DocumentBlock[];
  revision: number;
  sessionEpoch: number;
}

export type DocumentTimelineMode = "following-latest" | "reviewing-history";

export interface DocumentPaneState {
  activeTab: EditorTab;
  instruction: string;
  selectedIds: string[];
  lastSelectedId: string;
  selectedRequirementIds: string[];
  timeline: {
    mode: DocumentTimelineMode;
    unseenCount: number;
  };
  mutationState: DocumentMutationState;
  mutationError: ApiError | null;
  notice: string;
  agentSession: DocumentAgentSessionRecord;
}

export interface DocumentEditorSession {
  packId: string;
  acceptedPack: DocumentPack | null;
  workingBlocks: Record<DocumentType, DocumentBlock[]>;
  activeDocument: DocumentType;
  panes: Record<DocumentType, DocumentPaneState>;
  dirty: Record<DocumentType, boolean>;
  revision: Record<DocumentType, number>;
  loadStatus: AsyncStatus;
  saveState: Record<DocumentType, DocumentSaveState>;
  loadError: ApiError | null;
  saveError: Record<DocumentType, ApiError | null>;
  agentOwnerId: string;
}

const DOCUMENT_TYPES: DocumentType[] = ["cv", "cover_letter"];

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function reconcileBlocks(target: DocumentBlock[], incoming: DocumentBlock[]): void {
  const existingById = new Map(target.map((block) => [block.id, block]));
  const next = incoming.map((source) => {
    const existing = existingById.get(source.id);
    if (!existing) return clone(source);
    const record = existing as unknown as Record<string, unknown>;
    for (const key of Object.keys(record)) delete record[key];
    Object.assign(existing, clone(source));
    return existing;
  });
  target.splice(0, target.length, ...next);
}

function emptyPane(
  packId: string,
  documentType: DocumentType,
  now: string,
): DocumentPaneState {
  return {
    activeTab: "edit",
    instruction: "",
    selectedIds: [],
    lastSelectedId: "",
    selectedRequirementIds: [],
    timeline: { mode: "following-latest", unseenCount: 0 },
    mutationState: "idle",
    mutationError: null,
    notice: "",
    agentSession: {
      id: `document:${packId}:${documentType}`,
      packId,
      documentType,
      status: "idle",
      messages: [],
      updatedAt: now,
    },
  };
}

function emptySession(packId: string): DocumentEditorSession {
  const now = new Date().toISOString();
  return {
    packId,
    acceptedPack: null,
    workingBlocks: { cv: [], cover_letter: [] },
    activeDocument: "cv",
    panes: {
      cv: emptyPane(packId, "cv", now),
      cover_letter: emptyPane(packId, "cover_letter", now),
    },
    dirty: { cv: false, cover_letter: false },
    revision: { cv: 0, cover_letter: 0 },
    loadStatus: "idle",
    saveState: { cv: "saved", cover_letter: "saved" },
    loadError: null,
    saveError: { cv: null, cover_letter: null },
    agentOwnerId: "",
  };
}

export const useDocumentEditorStore = defineStore("documentEditor", () => {
  const sessions = reactive<Record<string, DocumentEditorSession>>({});
  const sessionEpoch = ref(0);
  let generatedId = 0;

  function messageId(prefix: string): string {
    generatedId += 1;
    return `${prefix}-${Date.now()}-${generatedId}`;
  }

  function paneFor(
    session: DocumentEditorSession,
    documentType = session.activeDocument,
  ): DocumentPaneState {
    return session.panes[documentType];
  }

  function persistAgentSession(
    session: DocumentEditorSession,
    documentType: DocumentType,
  ): void {
    if (session.agentOwnerId) {
      documentAgentSessionRepository.upsert(
        session.agentOwnerId,
        clone(session.panes[documentType].agentSession),
      );
    }
  }

  function appendAgentMessage(
    session: DocumentEditorSession,
    documentType: DocumentType,
    message: Omit<DocumentAgentMessage, "id" | "createdAt">,
  ): void {
    const createdAt = new Date().toISOString();
    const agentSession = session.panes[documentType].agentSession;
    agentSession.messages.push({
      ...message,
      id: messageId("message"),
      createdAt,
    });
    agentSession.updatedAt = createdAt;
    persistAgentSession(session, documentType);
  }

  function getOrCreateSession(packId: string): DocumentEditorSession {
    return (sessions[packId] ??= emptySession(packId));
  }

  function getSession(packId: string): DocumentEditorSession | undefined {
    return sessions[packId];
  }

  function hydrateAgentSession(
    packId: string,
    userId: string,
    documentType: DocumentType,
  ): void {
    const session = getOrCreateSession(packId);
    if (!userId) return;
    if (session.agentOwnerId && session.agentOwnerId !== userId) {
      const now = new Date().toISOString();
      for (const type of DOCUMENT_TYPES) {
        session.panes[type].agentSession = emptyPane(packId, type, now).agentSession;
      }
    }
    session.agentOwnerId = userId;
    const cached = documentAgentSessionRepository.read(userId, packId, documentType);
    if (cached) session.panes[documentType].agentSession = clone(cached);
  }

  function beginAgentRun(
    packId: string,
    userId: string,
    summary: {
      documentType: DocumentType;
      instruction: string;
      operation: string;
      selectedCount: number;
    },
  ): string {
    hydrateAgentSession(packId, userId, summary.documentType);
    const session = getOrCreateSession(packId);
    const agentSession = session.panes[summary.documentType].agentSession;
    const runId = messageId("run");
    agentSession.status = "running";
    appendAgentMessage(session, summary.documentType, {
      runId,
      kind: "request",
      title: summary.instruction,
      detail: summary.selectedCount
        ? `Apply to ${summary.selectedCount} selected block${summary.selectedCount === 1 ? "" : "s"}.`
        : `Apply to the full ${summary.documentType === "cv" ? "resume" : "cover letter"}.`,
    });
    appendAgentMessage(session, summary.documentType, {
      runId,
      kind: "tool-call",
      title: summary.operation,
      detail: `Input: document=${summary.documentType}; selection=${summary.selectedCount || "full document"}.`,
    });
    return runId;
  }

  function completeAgentRun(
    packId: string,
    documentType: DocumentType,
    runId: string,
    pack: DocumentPack,
  ): void {
    const session = getOrCreateSession(packId);
    const changes = (pack.change_log ?? [])
      .filter((entry) => entry.document_type === documentType)
      .at(-1)?.changes?.length ?? 0;
    session.panes[documentType].agentSession.status = "completed";
    appendAgentMessage(session, documentType, {
      runId,
      kind: "tool-result",
      title: "Document service completed",
      detail: `Output: version v${pack.version}; ${changes} recorded change${changes === 1 ? "" : "s"}.`,
    });
    appendAgentMessage(session, documentType, {
      runId,
      kind: "assistant",
      title: "Your document is ready to review",
      detail: changes
        ? `Review ${changes} grounded change${changes === 1 ? "" : "s"} in Changes.`
        : "The latest server document state is now visible.",
    });
  }

  function failAgentRun(
    packId: string,
    documentType: DocumentType,
    runId: string,
    error: ApiError,
  ): void {
    const session = getOrCreateSession(packId);
    session.panes[documentType].agentSession.status = "failed";
    appendAgentMessage(session, documentType, {
      runId,
      kind: "error",
      title: "Document service failed",
      detail: error.message,
    });
  }

  function beginLoad(packId: string, documentType: DocumentType): void {
    const session = getOrCreateSession(packId);
    session.loadStatus = "loading";
    session.loadError = null;
    paneFor(session, documentType).notice = "";
  }

  function acceptLoadedPack(packId: string, pack: DocumentPack): void {
    const session = getOrCreateSession(packId);
    const accepted = clone(pack);
    session.acceptedPack = accepted;
    for (const type of DOCUMENT_TYPES) {
      session.workingBlocks[type] = clone(accepted.documents[type]?.blocks ?? []);
      session.dirty[type] = false;
      session.revision[type] = 0;
      session.saveState[type] = "saved";
      session.saveError[type] = null;
    }
    session.loadStatus = "success";
    session.loadError = null;
    for (const type of DOCUMENT_TYPES) {
      const pane = paneFor(session, type);
      pane.mutationState = "idle";
      pane.mutationError = null;
      pane.notice = "";
      reconcileSelection(session, type);
    }
  }

  function failLoad(
    packId: string,
    error: ApiError,
    documentType: DocumentType,
  ): void {
    const session = getOrCreateSession(packId);
    session.loadStatus = "error";
    session.loadError = error;
    paneFor(session, documentType).notice = error.message;
  }

  function setActiveDocument(packId: string, type: DocumentType): void {
    const session = getOrCreateSession(packId);
    session.activeDocument = type;
  }

  function setActiveTab(packId: string, tab: EditorTab): void {
    const session = getOrCreateSession(packId);
    paneFor(session).activeTab = tab;
  }

  function setInstruction(
    packId: string,
    value: string,
    documentType?: DocumentType,
  ): void {
    const session = getOrCreateSession(packId);
    const pane = paneFor(session, documentType);
    pane.instruction = value;
    if (pane.mutationError) {
      pane.mutationError = null;
      pane.notice = "";
    }
  }

  function selectBlock(packId: string, blockId: string, modifiers: SelectionModifiers): void {
    const session = getOrCreateSession(packId);
    const pane = paneFor(session);
    const blocks = session.workingBlocks[session.activeDocument];
    if (!blocks.some((block) => block.id === blockId)) return;
    const selected = new Set(pane.selectedIds);
    if (modifiers.shiftKey && pane.lastSelectedId) {
      const start = blocks.findIndex((block) => block.id === pane.lastSelectedId);
      const end = blocks.findIndex((block) => block.id === blockId);
      if (start >= 0 && end >= 0) {
        for (const block of blocks.slice(Math.min(start, end), Math.max(start, end) + 1)) {
          selected.add(block.id);
        }
      }
    } else if (modifiers.toggleKey) {
      if (selected.has(blockId)) selected.delete(blockId);
      else selected.add(blockId);
    } else {
      selected.clear();
      selected.add(blockId);
    }
    pane.selectedIds = [...selected];
    pane.lastSelectedId = blockId;
    if (pane.mutationError) {
      pane.mutationError = null;
      pane.notice = "";
    }
  }

  function clearSelection(packId: string, documentType?: DocumentType): void {
    const session = getOrCreateSession(packId);
    const pane = paneFor(session, documentType);
    pane.selectedIds = [];
    pane.lastSelectedId = "";
  }

  function selectAllBlocks(packId: string): void {
    const session = getOrCreateSession(packId);
    const pane = paneFor(session);
    const blocks = session.workingBlocks[session.activeDocument];
    pane.selectedIds = blocks.map((block) => block.id);
    pane.lastSelectedId = blocks.at(-1)?.id ?? "";
  }

  function markDirty(session: DocumentEditorSession, type: DocumentType): void {
    session.dirty[type] = true;
    session.revision[type] += 1;
    session.saveState[type] = "dirty";
    session.saveError[type] = null;
    const pane = paneFor(session, type);
    if (pane.mutationError || pane.notice) {
      pane.mutationError = null;
      pane.notice = "";
    }
  }

  function updateBlock(packId: string, blockId: string, content: string): void {
    const session = getOrCreateSession(packId);
    const block = session.workingBlocks[session.activeDocument].find((item) => item.id === blockId);
    if (!block || block.content === content) return;
    block.content = content;
    markDirty(session, session.activeDocument);
  }

  function addLine(packId: string): string | null {
    const session = getOrCreateSession(packId);
    const pane = paneFor(session);
    const blocks = session.workingBlocks[session.activeDocument];
    const indexes = blocks
      .map((block, index) => (pane.selectedIds.includes(block.id) ? index : -1))
      .filter((index) => index >= 0);
    const insertAfter = indexes.length ? Math.max(...indexes) : blocks.length - 1;
    const previous = blocks[insertAfter];
    const id = globalThis.crypto?.randomUUID?.() ?? `document-block-${++generatedId}`;
    blocks.splice(insertAfter + 1, 0, {
      id,
      type: previous?.type === "bullet" ? "bullet" : "paragraph",
      content: "",
      evidence: [],
      verification_status: "rephrased",
      format: clone(previous?.format ?? {}),
    });
    pane.selectedIds = [id];
    pane.lastSelectedId = id;
    markDirty(session, session.activeDocument);
    return id;
  }

  function removeSelectedLines(packId: string): boolean {
    const session = getOrCreateSession(packId);
    const type = session.activeDocument;
    const pane = paneFor(session, type);
    const blocks = session.workingBlocks[type];
    const selected = new Set(pane.selectedIds);
    if (!selected.size) return false;
    const retained = blocks.filter((block) => !selected.has(block.id));
    if (!retained.length) {
      pane.notice = "The document must keep at least one line.";
      return false;
    }
    session.workingBlocks[type] = retained;
    clearSelection(packId, type);
    markDirty(session, type);
    return true;
  }

  function formatSelectedBlocks(
    packId: string,
    action: DocumentFormatAction,
    value = "",
  ): boolean {
    const session = getOrCreateSession(packId);
    const selected = new Set(paneFor(session).selectedIds);
    const blocks = session.workingBlocks[session.activeDocument].filter((block) => selected.has(block.id));
    if (!blocks.length) return false;
    if (action === "bold") {
      const bold = !blocks.every((block) => block.format?.bold);
      for (const block of blocks) block.format = { ...block.format, bold };
    } else if (action === "bullet") {
      const makeBullet = !blocks.every((block) => block.type === "bullet");
      for (const block of blocks) {
        if (["paragraph", "bullet"].includes(block.type)) block.type = makeBullet ? "bullet" : "paragraph";
      }
    } else {
      for (const block of blocks) block.format = { ...block.format, [action]: value };
    }
    markDirty(session, session.activeDocument);
    return true;
  }

  function captureSnapshot(
    packId: string,
    documentType?: DocumentType,
  ): DocumentRequestSnapshot | null {
    const session = sessions[packId];
    if (!session?.acceptedPack) return null;
    const type = documentType ?? session.activeDocument;
    return {
      packId,
      documentType: type,
      blocks: clone(session.workingBlocks[type]),
      revision: session.revision[type],
      sessionEpoch: sessionEpoch.value,
    };
  }

  function beginSave(packId: string, documentType: DocumentType): void {
    const session = getOrCreateSession(packId);
    session.saveState[documentType] = "saving";
    session.saveError[documentType] = null;
  }

  function failSave(packId: string, documentType: DocumentType, error: ApiError): void {
    const session = getOrCreateSession(packId);
    session.dirty[documentType] = true;
    session.saveState[documentType] = "error";
    session.saveError[documentType] = error;
    paneFor(session, documentType).notice = error.message;
  }

  function beginMutation(
    packId: string,
    mutation: Exclude<DocumentMutationState, "idle">,
    documentType?: DocumentType,
  ): void {
    const session = getOrCreateSession(packId);
    const pane = paneFor(session, documentType);
    pane.mutationState = mutation;
    pane.mutationError = null;
    pane.notice = "";
  }

  function failMutation(
    packId: string,
    error: ApiError,
    documentType?: DocumentType,
  ): void {
    const session = getOrCreateSession(packId);
    const pane = paneFor(session, documentType);
    pane.mutationState = "idle";
    pane.mutationError = error;
    pane.notice = error.message;
  }

  function reconcileSelection(
    session: DocumentEditorSession,
    documentType: DocumentType,
  ): void {
    const pane = paneFor(session, documentType);
    const available = new Set(session.workingBlocks[documentType].map((block) => block.id));
    pane.selectedIds = pane.selectedIds.filter((id) => available.has(id));
    if (!available.has(pane.lastSelectedId)) pane.lastSelectedId = "";
  }

  function acceptServerPack(snapshot: DocumentRequestSnapshot, pack: DocumentPack): boolean {
    if (snapshot.sessionEpoch !== sessionEpoch.value) return false;
    const session = sessions[snapshot.packId];
    if (!session) return false;
    session.acceptedPack = clone(pack);
    const matches = session.revision[snapshot.documentType] === snapshot.revision;
    for (const type of DOCUMENT_TYPES) {
      if (type === snapshot.documentType) {
        if (matches) {
          reconcileBlocks(
            session.workingBlocks[type],
            pack.documents[type]?.blocks ?? snapshot.blocks,
          );
          session.dirty[type] = false;
          session.saveState[type] = "saved";
          session.saveError[type] = null;
        } else {
          session.dirty[type] = true;
          session.saveState[type] = "dirty";
          session.saveError[type] = null;
        }
      } else if (!session.dirty[type]) {
        reconcileBlocks(session.workingBlocks[type], pack.documents[type]?.blocks ?? []);
      }
    }
    const pane = paneFor(session, snapshot.documentType);
    pane.mutationState = "idle";
    pane.mutationError = null;
    pane.notice = "";
    reconcileSelection(session, snapshot.documentType);
    return matches;
  }

  function packFor(packId: string): DocumentPack | null {
    const session = sessions[packId];
    if (!session?.acceptedPack) return null;
    const pack = clone(session.acceptedPack);
    for (const type of DOCUMENT_TYPES) pack.documents[type].blocks = clone(session.workingBlocks[type]);
    return pack;
  }

  function blocksFor(packId: string, documentType?: DocumentType): DocumentBlock[] {
    const session = sessions[packId];
    if (!session) return [];
    return clone(session.workingBlocks[documentType ?? session.activeDocument]);
  }

  function beforeBlocksFor(packId: string): DocumentBlock[] {
    const session = sessions[packId];
    if (!session?.acceptedPack || session.activeDocument !== "cv") return [];
    const stored = session.acceptedPack.before_documents?.cv?.blocks ?? [];
    if (stored.length) return clone(stored);
    return (session.acceptedPack.source_cv?.content ?? "")
      .split(/\r?\n/)
      .map((content) => content.trim())
      .filter(Boolean)
      .map((content, index) => ({
        id: `source-${index}`,
        type: index === 0 ? "heading" : "paragraph",
        content: content.replace(/^#{1,6}\s*/, ""),
        verification_status: "verified",
      }));
  }

  function selectedBlocksFor(packId: string): DocumentBlock[] {
    const session = sessions[packId];
    if (!session) return [];
    const selected = new Set(paneFor(session).selectedIds);
    return clone(session.workingBlocks[session.activeDocument].filter((block) => selected.has(block.id)));
  }

  function wordCountFor(packId: string): number {
    return blocksFor(packId).reduce(
      (total, block) => total + block.content.split(/\s+/).filter(Boolean).length,
      0,
    );
  }

  function selectionLabelFor(packId: string): string {
    const session = sessions[packId];
    const count = session ? paneFor(session).selectedIds.length : 0;
    return count ? `Editing: ${count} selected block${count === 1 ? "" : "s"}` : "Editing: full document";
  }

  function quickActionsFor(packId: string): [string, string][] {
    return sessions[packId]?.activeDocument === "cover_letter"
      ? [["formal", "Make more formal"], ["short", "Make it shorter"], ["strong", "Strengthen the message"], ["technical", "Tailor to technical role"]]
      : [["strong", "Make bullet stronger"], ["short", "Make it shorter"], ["impact", "Action + impact"], ["technical", "Tailor to technical role"]];
  }

  function requirementsFor(packId: string, supported: boolean): string[] {
    const analysis = sessions[packId]?.acceptedPack?.analysis;
    if (!analysis) return [];
    const optimisation = analysis.llm_optimisation;
    if (optimisation?.used) {
      return (optimisation.requirements ?? [])
        .filter((item) => item.status === (supported ? "supported" : "gap"))
        .map((item) => item.requirement);
    }
    return clone(supported ? analysis.matched ?? [] : analysis.unsupported ?? []);
  }

  function supportedRequirementsFor(packId: string): string[] {
    return requirementsFor(packId, true);
  }

  function unsupportedRequirementsFor(packId: string): string[] {
    return requirementsFor(packId, false);
  }

  function setNotice(
    packId: string,
    notice: string,
    documentType?: DocumentType,
  ): void {
    const session = getOrCreateSession(packId);
    paneFor(session, documentType).notice = notice;
  }

  function dismissNotice(packId: string, documentType?: DocumentType): void {
    const session = getOrCreateSession(packId);
    const pane = paneFor(session, documentType);
    pane.notice = "";
    pane.mutationError = null;
  }

  function toggleRequirement(packId: string, requirementId: string): void {
    const session = getOrCreateSession(packId);
    const pane = paneFor(session);
    const selected = new Set(pane.selectedRequirementIds);
    if (selected.has(requirementId)) selected.delete(requirementId);
    else selected.add(requirementId);
    pane.selectedRequirementIds = [...selected];
  }

  function clearRequirementSelection(packId: string, documentType?: DocumentType): void {
    const session = getOrCreateSession(packId);
    const pane = paneFor(session, documentType);
    pane.selectedRequirementIds = [];
  }

  function removeSession(packId: string): void {
    delete sessions[packId];
  }

  function reset(): void {
    sessionEpoch.value += 1;
    for (const packId of Object.keys(sessions)) delete sessions[packId];
  }

  return {
    sessions,
    sessionEpoch,
    getOrCreateSession,
    getSession,
    hydrateAgentSession,
    beginAgentRun,
    completeAgentRun,
    failAgentRun,
    beginLoad,
    acceptLoadedPack,
    failLoad,
    setActiveDocument,
    setActiveTab,
    setInstruction,
    selectBlock,
    selectAllBlocks,
    clearSelection,
    updateBlock,
    addLine,
    removeSelectedLines,
    formatSelectedBlocks,
    captureSnapshot,
    beginSave,
    failSave,
    beginMutation,
    failMutation,
    acceptServerPack,
    packFor,
    blocksFor,
    beforeBlocksFor,
    selectedBlocksFor,
    wordCountFor,
    selectionLabelFor,
    quickActionsFor,
    supportedRequirementsFor,
    unsupportedRequirementsFor,
    setNotice,
    dismissNotice,
    toggleRequirement,
    clearRequirementSelection,
    removeSession,
    reset,
  };
});
